// src/belediye/components/utils/InfoPopover.js
//
// Paylaşılan "Bilgi" çipi + içerik — tüm sayfalarda AYNI.
//   • Masaüstü: butona çıpalı açılır panel (popover)
//   • Mobil:    ALTTAN açılan çekmece (bottom-sheet) + karartma (backdrop)
//               (FilterSheet ile aynı davranış; React Portal ile body'ye render
//                edilir → sticky/sidebar stacking-context'lerinin altında kalmaz.)
//
// Props:
//   title      — başlık metni
//   label      — buton metni (iconOnly=false iken)
//   iconOnly   — yalnız ikon (yazısız, kompakt) buton — mobilde başlık yanı için
//   children   — popover/sheet içeriği (ul/li vb.)
//
// Kullanım:
//   <InfoPopover title="Nasıl çalışır?">{/* içerik */}</InfoPopover>
//   <InfoPopover iconOnly title="Nasıl çalışır?">{/* içerik */}</InfoPopover>

import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  InformationCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

import "./InfoPopover.css";

const useIsMobile = (max = 768) => {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia(`(max-width: ${max}px)`).matches
      : false
  );
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mm = window.matchMedia(`(max-width: ${max}px)`);
    const onChange = (e) => setIsMobile(!!e.matches);
    if (mm.addEventListener) mm.addEventListener("change", onChange);
    else mm.addListener(onChange);
    setIsMobile(mm.matches);
    return () => {
      if (mm.removeEventListener) mm.removeEventListener("change", onChange);
      else mm.removeListener(onChange);
    };
  }, [max]);
  return isMobile;
};

export default function InfoPopover({
  title = "Bilgi",
  label = "Bilgi",
  iconOnly = false,
  wide = false,
  children,
}) {
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile(768);

  const wrapRef = useRef(null);
  const panelRef = useRef(null);

  // Masaüstü: dışarı tıkla / Escape ile kapan (mobilde backdrop kapatır)
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && wrapRef.current.contains(e.target)) return;
      if (panelRef.current && panelRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Mobil sheet açıkken arka planı kilitle
  useEffect(() => {
    if (!(open && isMobile)) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open, isMobile]);

  const panelInner = (
    <>
      <div className="ui-info-head">
        <span className="ui-info-head-title">{title}</span>
        <button
          type="button"
          className="ui-info-close"
          onClick={() => setOpen(false)}
          aria-label="Kapat"
        >
          <XMarkIcon />
        </button>
      </div>
      <div className="ui-info-body">{children}</div>
    </>
  );

  return (
    <div className="ui-info-wrap" ref={wrapRef}>
      <button
        type="button"
        className={`ui-info-btn${iconOnly ? " ui-info-btn--icon" : ""}${
          open ? " is-active" : ""
        }`}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        title={title}
      >
        <InformationCircleIcon />
        {iconOnly ? null : <span>{label}</span>}
      </button>

      {/* Masaüstü: çıpalı popover */}
      {open && !isMobile ? (
        <div
          className={`ui-info-popover pop-ok${wide ? " ui-info-popover--wide" : ""}`}
          data-ok="ust"
          data-ok-x="sag"
          role="dialog"
          ref={panelRef}
        >
          {panelInner}
        </div>
      ) : null}

      {/* Mobil: portal ile alttan çekmece + backdrop (her şeyin üstünde) */}
      {open && isMobile
        ? createPortal(
            <div className="ui-info-overlay">
              <div
                className="ui-info-backdrop"
                onClick={() => setOpen(false)}
                aria-hidden="true"
              />
              <div className="ui-info-sheet" role="dialog" ref={panelRef}>
                <div className="ui-info-grabber" aria-hidden="true" />
                {panelInner}
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
